import { FormControl, Grid, InputAdornment, TextField, Tooltip } from "@mui/material";

import React, { memo, useCallback, useEffect, useRef, useState, KeyboardEvent } from "react";

import { FieldError } from "@components/FieldError";
import { ITextFormControlProperty } from "@definitions/definitions";
import { customDisabledOutlineInput, flexCenterProps, horizontalFormControlGridLeftStyle, paddingX1Props, requiredLabelStyle } from "@styles/global-css-class";
import { StyledFormLabel } from "@components/Common";
import { EDITING_KEYS, NAVIGATION_KEYS, NUMBER_VALID_KEYS } from "@constants/common";
import { colors } from "@components/ThemeRegistry/theme";
import DiffInheritanceIndicator from "@components/DiffInheritanceIndicator";

function NewOnlyTextFormControl({
	childKey,
	label,
	value,
	name,
	defaultValue,
	placeHolder,
	isRequired,
	isDisabled,
	isHidden = false,
	onChange,
	formState,
	xsFormControl = 12,
	mdFormControl = 12,
	xsLabel = 0,
	xsInput,
	textAlign,
	textErrorAlign,
	itemSx,
	textSx,
	isExpanded,
	isHideLabel = true,
	labelToolTip,
	hasEndAdornment,
	endAdornmentLabel,
	itemFocus,
	onBlur,
	onlyNumber,
	formatValue,
	showIndicator,
	errorName,
	isReturnId,
	id,
	isIndicatorTakeSpace,
	...props
}: Readonly<ITextFormControlProperty>) {
	const inputRef = useRef<HTMLInputElement>(null);
	const [valueInput, setValueInput] = useState(value ?? defaultValue ?? '');

	useEffect(() => {
		if (itemFocus) {
			inputRef.current?.focus();
		}
	}, [itemFocus]);

	useEffect(() => {
		if (isExpanded && inputRef.current) {
			inputRef.current?.focus();
		}
	}, [isExpanded, inputRef.current])

	useEffect(() => {
		setValueInput(value ?? '');
	}, [value])

	const handleOnBlur = useCallback((event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>) => {
		if (isReturnId) {
			if (onBlur) {
				onChange({ value: event.target.value, id: id });
				return onBlur({ value: event.target.value, id: id });
			}
			return onChange({ value: event.target.value, id: id });
		}
		else {
			if (onBlur) {
				onChange(event.target.value);
				return onBlur(event.target.value);
			}
			return onChange(event.target.value);
		}
	}, [onBlur, onChange])

	const handleNumberKey = useCallback((e: KeyboardEvent) => {
		const numValidKeys = [...NUMBER_VALID_KEYS, ...NAVIGATION_KEYS, ...EDITING_KEYS];
		if (onlyNumber && !numValidKeys.includes(e.key)) {
			return e.preventDefault();
		}
	}, [onlyNumber]);

	return <Grid container item xs={xsFormControl} md={mdFormControl} sx={itemSx ?? {
		...paddingX1Props,
		alignContent: 'flex-start'
	}}>
		<FormControl key={`${childKey}-form-control`} fullWidth margin='normal' {...props}>
			<Grid container item sx={flexCenterProps}>
				{!isHideLabel && <Grid item xs={xsLabel} sx={itemSx ?? horizontalFormControlGridLeftStyle}>
					<StyledFormLabel
						aria-describedby={name}
						hidden={isHidden}
						error={formState?.errors[errorName ?? name] !== undefined}
						sx={textSx ?? {
							...requiredLabelStyle,
							textAlign: textAlign ?? "right",
						}}>
						{label}
					</StyledFormLabel>
				</Grid>}
				<Grid container item xs={xsInput ?? (xsLabel === 12 ? 12 : 12 - xsLabel)} sx={{ position: "relative" }}>
					{labelToolTip ? <Tooltip title={labelToolTip}>
						<TextField
							type='text'
							id={name}
							key={`${childKey}-input`}
							name={name}
							placeholder={placeHolder}
							error={formState?.errors[errorName ?? name] !== undefined}
							inputRef={inputRef}
							disabled={isDisabled ?? false}
							hidden={isHidden}
							required={isRequired}
							label={isHideLabel ? label : undefined}
							spellCheck
							value={valueInput}
							onChange={(e) => {
								setValueInput(formatValue ? formatValue(e.target.value) : e.target.value)
							}}
							onKeyDown={handleNumberKey}
							onBlur={handleOnBlur}
							sx={{
								width: "100%",
								...customDisabledOutlineInput
							}}
							InputProps={{
								endAdornment: hasEndAdornment ?
									(<InputAdornment position="end">{endAdornmentLabel}</InputAdornment>)
									: null,
								sx: {
									backgroundColor: colors.white,
								}
							}}
						/>
					</Tooltip> :
						<TextField
							type='text'
							id={name}
							key={`${childKey}-input`}
							name={name}
							placeholder={placeHolder}
							error={formState?.errors[errorName ?? name] !== undefined}
							disabled={isDisabled ?? false}
							hidden={isHidden}
							inputRef={inputRef}
							required={isRequired}
							label={isHideLabel ? label : undefined}
							spellCheck
							value={valueInput}
							onChange={(e) => {
								setValueInput(formatValue ? formatValue(e.target.value) : e.target.value)
							}}
							onKeyDown={handleNumberKey}
							onBlur={handleOnBlur}
							sx={{
								width: "100%",
								...customDisabledOutlineInput,
							}}
							InputProps={{
								endAdornment: hasEndAdornment ?
									(<InputAdornment position="end">{endAdornmentLabel}</InputAdornment>)
									: null,
								sx: {
									backgroundColor: colors.white,
								}
							}}
						/>}
					{showIndicator !== undefined && showIndicator() === true && (
						<Grid sx={{ position: "absolute", right: 0, top: "50%", transform: "translate(18px, -50%)" }}>
							<DiffInheritanceIndicator />
						</Grid>
					)}
				</Grid>
				{formState && <FieldError key={`${childKey}-form-control-error`} formState={formState} name={errorName ?? name} textAlign={textErrorAlign} />}
			</Grid>

		</FormControl>
	</Grid>;
}

export default memo(NewOnlyTextFormControl);
